import type { CellContext } from '@tanstack/react-table';
import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import ExcelJS from 'exceljs';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@daater/api';
import { useNavigate } from '@tanstack/react-router';
import {
  ChartBarBigIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileSpreadsheet,
  LogOut,
  Menu,
  Package,
  X,
} from 'lucide-react';
import {
  queryKeys,
  useCurrentTeamQuery,
  useDeleteSavedReportMutation,
  useDatasetsQuery,
  useSavedReportsQuery,
  useSwitchAccountMutation,
  useTableFieldsQuery,
  useTableFiltersQuery,
  useTableRecordsQuery,
  useTablesQuery,
  useUpdateSettingsMutation,
} from '../api/queries';
import type { TableColumnSettings } from '../api/types';
import type {
  ColumnMapping,
  Dataset,
  PendingSavedSearchPayload,
  SavedReportItem,
  SavedReportListResponse,
  TableField,
  TableFilter,
} from '../api/types';
import { useAccountStore } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';
import {
  createReport,
  fetchAutocompleteOptions,
  fetchReportStatus,
  postAnalyticsEvent,
} from '../api/endpoints';
import { useSessionState, clearDashboardSession } from '../hooks/useSessionState';
import SearchCards from '../components/search_cards';
import AnalysisSection from '../components/analysis-section';
import DataSection from '../components/data-section';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import SavedSearchesSection from '../components/saved-searches-section';
import { useOpenSavedSearch } from '../hooks/useOpenSavedSearch';
import {
  clearPendingSavedSearchPayload,
  loadPendingSavedSearchPayload,
  PENDING_SAVED_SEARCH_EVENT,
} from '../lib/saved-searches';
import {
  createDefaultFilterValueState,
  type FilterValueState,
  type DateRange,
  type TeamDetailRestrictions,
  hydrateFiltersFromHistoric,
  buildFieldsPayload,
  buildFieldInfo,
  validateDateRange,
  buildQueryFromFilters,
  getCountryFlag,
  parseDateRangeFromTableName,
  createEmptyFilterValues,
  applyDateRangeToFilters,
  generateYearQuickSelects,
  formatLookupSelectionLabel,
  fieldIdsToVisibilityState,
  isNumericIdentifier,
  getDisplayColumnHeader,
  getMappingColumnName,
  columnsToVisibilityState,
  visibilityStateToColumns,
  canAccessDetailedMode,
  shouldUseDemoMode,
  separateFiltersByDetailMode,
  limitDemoRecords,
  extractApiErrorMessage,
  extractReportFilePath,
  isReportGenerationInProgressError,
} from './table-data-detail.utils';
import type { VisibilityState } from '@tanstack/react-table';
import { DetailModeSwitch } from '../components/detail-mode-switch';
import { FilterFieldRenderer } from './table-data-detail/filters/FilterFieldRenderer';

type ActiveFilterPayload = TableFilter & {
  value: string | string[];
  conditions: string | undefined;
  filterValue: string | string[];
  fieldData: TableField | undefined;
};

const LOOKUP_FILTER_OPTIONS_LIMIT = 120;

const isDateValue = (value: unknown) => {
  if (typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}/.test(value);
};

const buildColumns = (
  mapping: ColumnMapping[] | undefined,
  sampleRow?: Record<string, unknown>
): ColumnDef<Record<string, unknown>>[] => {
  if (!mapping?.length) {
    if (!sampleRow) return [];
    return Object.keys(sampleRow).map(key => ({
      accessorKey: key,
      header: key,
      cell: (info: CellContext<Record<string, unknown>, unknown>) => {
        const value = info.getValue();
        if (value === null || value === undefined) return '-';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return String(value);
        }
        return '-';
      },
      filterFn:
        typeof sampleRow[key] === 'number'
          ? 'numberRange'
          : isDateValue(sampleRow[key])
            ? 'dateRange'
            : 'includesString',
    }));
  }

  return mapping
    .map(column => {
      const accessorKey = column.optionalName || column.internalName || column.name || column._id;
      if (!accessorKey) return null;

      const sampleValue = sampleRow ? sampleRow[accessorKey] : undefined;
      const filterFnValue =
        typeof sampleValue === 'number'
          ? 'numberRange'
          : isDateValue(sampleValue)
            ? 'dateRange'
            : 'includesString';

      return {
        accessorKey,
        header: getDisplayColumnHeader(column, mapping, sampleRow),
        cell: (info: CellContext<Record<string, unknown>, unknown>) => {
          const value = info.getValue();
          if (value === null || value === undefined) return '-';
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            return String(value);
          }
          return '-';
        },
        filterFn: filterFnValue,
      };
    })
    .filter(Boolean) as ColumnDef<Record<string, unknown>>[];
};

const inputClassName =
  'block w-full rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-20 text-sm py-3 px-3 transition-shadow';

const sidebarTabClassName =
  'flex items-center gap-3 rounded-lg px-4 py-3 text-[13px] transition-all duration-200';

const HISTORIC_STORAGE_KEY = 'historicSearch';

const formatReportTimestamp = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const toExcelCellValue = (value: unknown): string | number | boolean | Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'symbol') {
    return value.description ?? 'symbol';
  }
  if (typeof value === 'function') {
    return '[function]';
  }
  if (Array.isArray(value)) {
    return value.map(item => String(item ?? '')).join(', ');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '[object]';
  }
};

export default function TableDataDetailScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const account = useAccountStore(state => state.account);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const [selectedSection, setSelectedSection] = useSessionState<string | undefined>(
    'selectedSection',
    undefined
  );
  const [selectedDataset, setSelectedDataset] = useSessionState<string | undefined>(
    'selectedDataset',
    account?.dataset
  );
  const [appliedTableId, setAppliedTableId] = useSessionState<string | undefined>(
    'appliedTableId',
    account?.table
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useSessionState('sidebarCollapsed', false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  const switchAccountMutation = useSwitchAccountMutation();
  const updateSettingsMutation = useUpdateSettingsMutation();
  const sidebarTheme = (import.meta.env.VITE_SIDEBAR_THEME ?? '').toLowerCase().trim();
  const isWhiteBlueSidebar =
    sidebarTheme === 'blanco-azul' ||
    sidebarTheme === 'blanco-auzl' ||
    sidebarTheme === 'white-blue';
  const sidebarClasses = {
    container: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-900 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)]'
      : 'border-white/10 bg-gradient-to-b from-[#0d2147]/90 via-[#0b1c3d]/90 to-[#08152f]/90 text-white shadow-[0_20px_80px_-30px_rgba(6,12,28,0.9)] backdrop-blur-2xl',
    logo: isWhiteBlueSidebar
      ? ''
      : 'brightness-0 invert drop-shadow-[0_6px_16px_rgba(255,255,255,0.15)]',
    iconButton: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900'
      : 'border-white/20 bg-white/5 text-white/80 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.45)] hover:bg-white/10 hover:text-white',
    sectionLabel: isWhiteBlueSidebar ? 'text-slate-500' : 'text-white/50',
    selectFlag: isWhiteBlueSidebar
      ? 'bg-slate-100 text-slate-700 shadow-[0_8px_20px_-16px_rgba(15,23,42,0.25)]'
      : 'bg-white/10 text-white shadow-[0_8px_20px_-16px_rgba(0,0,0,0.7)]',
    select: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-900 shadow-[0_10px_24px_-16px_rgba(15,23,42,0.2)] focus:border-primary focus:ring focus:ring-primary/20'
      : 'border-white/15 bg-white/10 text-white shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)] backdrop-blur-md focus:border-primary focus:ring focus:ring-primary/20',
    selectCaret: isWhiteBlueSidebar ? 'text-slate-400' : 'text-blue-200',
    baseDivider: isWhiteBlueSidebar ? 'border-slate-200' : 'border-white/10',
    baseActive: isWhiteBlueSidebar
      ? 'bg-primary text-white shadow-[0_12px_24px_-16px_rgba(37,99,235,0.5)] font-semibold border border-primary/30 ring-1 ring-primary/20'
      : 'bg-gradient-to-r from-white/30 via-white/15 to-white/5 text-white shadow-[0_16px_30px_-20px_rgba(0,0,0,0.7)] font-semibold border border-white/40 ring-1 ring-white/20',
    baseInactive: isWhiteBlueSidebar
      ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 font-normal border border-transparent'
      : 'text-blue-100 hover:bg-white/10 hover:text-white font-normal border border-transparent',
    baseIcon: isWhiteBlueSidebar
      ? 'h-6 w-6 inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center p-0.5 text-primary text-sm rounded-full bg-primary/10 border-primary/30 shadow-[inset_0_1px_0px_rgba(255,255,255,0.9),0_0_8px_rgba(37,99,235,0.25),0_3px_8px_rgba(15,23,42,0.12)] hover:bg-primary/15 transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-white/70 before:via-transparent before:to-transparent before:opacity-70 before:pointer-events-none after:absolute after:inset-0 after:rounded-full after:bg-gradient-to-tl after:from-primary/20 after:via-transparent after:to-transparent after:opacity-60 after:pointer-events-none antialiased'
      : 'h-6 w-6 inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center p-0.5 text-white text-sm font-medium rounded-full bg-white/2.5 border border-white/50 backdrop-blur-sm shadow-[inset_0_1px_0px_rgba(255,255,255,0.75),0_0_9px_rgba(0,0,0,0.2),0_3px_8px_rgba(0,0,0,0.15)] hover:bg-white/30 transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-white/60 before:via-transparent before:to-transparent before:opacity-70 before:pointer-events-none after:absolute after:inset-0 after:rounded-full after:bg-gradient-to-tl after:from-white/30 after:via-transparent after:to-transparent after:opacity-50 after:pointer-events-none transition antialiased',
    emptyText: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-100',
    collapsedLabel: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-200',
    collapsedFlag: isWhiteBlueSidebar
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-white/20 bg-white/10 text-white',
    collapsedBaseActive: isWhiteBlueSidebar
      ? 'border-primary bg-primary text-white'
      : 'border-white/50 bg-gradient-to-br from-white/35 via-white/20 to-white/5 text-white',
    collapsedBaseInactive: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      : 'border-white/10 bg-white/5 text-blue-100 hover:bg-white/10 hover:text-white',
    menuButton: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-800 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.25)] hover:bg-slate-50'
      : 'border-white/15 bg-white/10 text-white shadow-[0_16px_36px_-24px_rgba(0,0,0,0.7)] backdrop-blur-md hover:bg-white/15',
    menuSubText: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-200',
    menuChevron: isWhiteBlueSidebar ? 'text-slate-400' : 'text-blue-200',
  };

  const { data: datasets = [], isLoading: datasetsLoading } = useDatasetsQuery(Boolean(token));
  const { data: currentTeam } = useCurrentTeamQuery(user?._id);
  const sectionData = React.useMemo(() => {
    const bySection = new Map<string, Dataset[]>();
    datasets.forEach(dataset => {
      const section =
        typeof dataset.section === 'string' && dataset.section.trim() ? dataset.section : 'General';
      const items = bySection.get(section) ?? [];
      items.push(dataset);
      bySection.set(section, items);
    });
    return { list: Array.from(bySection.keys()), bySection };
  }, [datasets]);
  const datasetsForSection = React.useMemo(() => {
    if (selectedSection) {
      return sectionData.bySection.get(selectedSection) ?? [];
    }
    return datasets;
  }, [selectedSection, sectionData.bySection, datasets]);
  const datasetId = selectedDataset ?? datasetsForSection[0]?._id;
  const { data: tableList, isLoading: tablesLoading } = useTablesQuery(datasetId);
  const tables = React.useMemo(() => tableList?.items ?? [], [tableList]);
  const { data: savedReportsData, isLoading: savedReportsLoading } =
    useSavedReportsQuery(datasetId);
  const savedReports = React.useMemo(() => savedReportsData?.items ?? [], [savedReportsData]);
  const activeTableId = appliedTableId;
  const accountUserId = typeof account?.user === 'string' ? account.user : account?.user?._id;
  const userId = accountUserId ?? user?._id;
  const openSavedSearch = useOpenSavedSearch();
  const deleteSavedReportMutation = useDeleteSavedReportMutation(datasetId);
  const userData = account?.userData as Record<string, unknown> | undefined;
  const userFirstName = typeof userData?.firstName === 'string' ? userData.firstName : '';
  const userLastName = typeof userData?.lastName === 'string' ? userData.lastName : '';
  const userFullName = `${userFirstName} ${userLastName}`.trim();
  const resolvedEmail =
    user?.email ?? (typeof userData?.email === 'string' ? userData.email : '') ?? '';
  const displayName =
    userFullName ||
    (typeof user?.name === 'string' && user.name.trim() ? user.name.trim() : '') ||
    resolvedEmail ||
    'Usuario';
  const initials = displayName.slice(0, 2).toUpperCase();
  const accountPlan: unknown =
    (account as Record<string, unknown>)?.plan ??
    (typeof (account as Record<string, unknown>)?.teamData === 'object' &&
    (account as Record<string, unknown>)?.teamData !== null
      ? ((account as Record<string, unknown>)?.teamData as Record<string, unknown>)?.plan
      : undefined);
  const accountDaysLeft: unknown =
    (account as Record<string, unknown>)?.daysLeft ??
    (typeof (account as Record<string, unknown>)?.teamData === 'object' &&
    (account as Record<string, unknown>)?.teamData !== null
      ? ((account as Record<string, unknown>)?.teamData as Record<string, unknown>)?.daysLeft
      : undefined);
  const accountEmail = resolvedEmail || 'Usuario';

  // Detailed mode state
  const [detailActived, setDetailActived] = useSessionState('detailActived', false);
  const [demoMode, setDemoMode] = useSessionState('demoMode', false);
  const [searchTrigger, setSearchTrigger] = React.useState(0);

  const [appliedQuery, setAppliedQuery] = React.useState<string | undefined>();
  const {
    data: recordsData,
    isLoading: recordsLoading,
    isFetching: recordsFetching,
    error: recordsError,
  } = useTableRecordsQuery(activeTableId, 1, appliedQuery, detailActived, demoMode, searchTrigger);
  const { data: fields = [], isLoading: fieldsLoading } = useTableFieldsQuery(activeTableId);
  const { data: filtersData, isLoading: filtersLoading } = useTableFiltersQuery(activeTableId);
  const [filterValues, setFilterValues] = React.useState<Record<string, FilterValueState>>({});
  const [moreFiltersActive, setMoreFiltersActive] = useSessionState('moreFiltersActive', false);
  const [pendingHistoricSearch, setPendingHistoricSearch] = React.useState<Record<
    string,
    unknown
  > | null>(null);
  const [pendingSavedSearch, setPendingSavedSearch] =
    React.useState<PendingSavedSearchPayload | null>(null);
  const [historicApplied, setHistoricApplied] = React.useState(false);
  const [savedSearchApplied, setSavedSearchApplied] = React.useState(false);
  const [searchRequested, setSearchRequested] = React.useState(false);
  const [hasSearchRun, setHasSearchRun] = React.useState(false);
  const [searchPayload, setSearchPayload] = React.useState<Record<string, unknown> | null>(null);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [isExportingReport, setIsExportingReport] = React.useState(false);
  const [isSavingReport, setIsSavingReport] = React.useState(false);
  const [savedReport, setSavedReport] = React.useState(false);
  const [saveFeedback, setSaveFeedback] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [openingSavedReportId, setOpeningSavedReportId] = React.useState<string | null>(null);
  const [deletingSavedReportId, setDeletingSavedReportId] = React.useState<string | null>(null);
  const [activeColumnVisibility, setActiveColumnVisibility] = React.useState<VisibilityState>({});
  const [tableFilteredRows, setTableFilteredRows] = React.useState<Array<Record<string, unknown>>>(
    []
  );
  const exportTimerRef = React.useRef<number | null>(null);
  const exportFileRef = React.useRef<string | null>(null);

  // Guard: only sync account store → local state once (initial hydration).
  // Subsequent account store changes (e.g. from refetchOnWindowFocus picking up
  // another tab's switchAccount call) must NOT overwrite local selections.
  // Also skip if session already restored selections (tab discard recovery).
  const hasInitialSyncRef = React.useRef(
    Boolean(account?.dataset) ||
      Boolean(account?.table) ||
      Boolean(selectedDataset) ||
      Boolean(appliedTableId)
  );

  React.useEffect(() => {
    if (hasInitialSyncRef.current) return;
    if (account?.dataset || account?.table) {
      hasInitialSyncRef.current = true;
      if (account?.dataset) setSelectedDataset(account.dataset);
      if (account?.table) setAppliedTableId(account.table);
    }
  }, [account?.dataset, account?.table, setAppliedTableId, setSelectedDataset]);

  React.useEffect(() => {
    // Hide header on desktop, show on mobile
    document.body.classList.add('hide-header-desktop');
    return () => {
      document.body.classList.remove('hide-header-desktop');
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (exportTimerRef.current !== null) {
        window.clearInterval(exportTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!selectedDataset && datasetId) setSelectedDataset(datasetId);
  }, [datasetId, selectedDataset, setSelectedDataset]);

  React.useEffect(() => {
    if (selectedSection || !sectionData.list.length) return;
    const selected = datasets.find(
      dataset => dataset._id === selectedDataset || dataset._id === account?.dataset
    );
    const section =
      selected && typeof selected.section === 'string' && selected.section.trim()
        ? selected.section
        : sectionData.list[0];
    if (section) {
      setSelectedSection(section);
    }
  }, [
    selectedSection,
    sectionData.list,
    datasets,
    selectedDataset,
    account?.dataset,
    setSelectedSection,
  ]);

  React.useEffect(() => {
    if (!selectedSection) return;
    if (!datasetsForSection.length) return;
    if (selectedDataset && datasetsForSection.some(dataset => dataset._id === selectedDataset)) {
      return;
    }
    setSelectedDataset(datasetsForSection[0]?._id);
  }, [selectedSection, datasetsForSection, selectedDataset, setSelectedDataset]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [popupPosition, setPopupPosition] = React.useState({ bottom: 0, left: 0 });
  React.useLayoutEffect(() => {
    if (!menuOpen || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    setPopupPosition({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
    });
  }, [menuOpen]);

  React.useEffect(() => {
    setAppliedQuery(undefined);
    setFilterValues({});
    setSearchRequested(false);
    setHasSearchRun(false);
    setSearchPayload(null);
    setSearchError(null);
    setSaveFeedback(null);
    setSaveError(null);
    // Reset detailed mode when switching tables
    setDetailActived(false);
    setDemoMode(false);
    setSavedReport(false);
    setSavedSearchApplied(false);
  }, [activeTableId, setDemoMode, setDetailActived]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(HISTORIC_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      setPendingHistoricSearch(parsed);
    } catch {
      window.localStorage.removeItem(HISTORIC_STORAGE_KEY);
    }
  }, []);

  React.useEffect(() => {
    const loadPendingSavedSearch = () => {
      const payload = loadPendingSavedSearchPayload();
      if (!payload) return;
      setSavedSearchApplied(false);
      setPendingSavedSearch(payload);
    };

    loadPendingSavedSearch();
    window.addEventListener(PENDING_SAVED_SEARCH_EVENT, loadPendingSavedSearch);
    return () => window.removeEventListener(PENDING_SAVED_SEARCH_EVENT, loadPendingSavedSearch);
  }, []);

  const handleTableApply = async (nextTableId: string) => {
    if (!account?._id || !datasetId) return;

    setAppliedTableId(nextTableId);

    await switchAccountMutation.mutateAsync({
      account: account._id,
      dataset: datasetId,
      table: nextTableId,
    });

    void navigate({ to: `/data/table/${nextTableId ?? ''}` });
  };

  const handleClearFilters = () => {
    const emptyValues = createEmptyFilterValues(mappedFilters);

    // Re-apply date range from current table if available
    const currentTable = tables.find(t => t._id === appliedTableId);
    if (currentTable?.name) {
      const dateRange = parseDateRangeFromTableName(currentTable.name);
      if (dateRange) {
        const valuesWithDateRange = applyDateRangeToFilters(mappedFilters, emptyValues, dateRange);
        setFilterValues(valuesWithDateRange);
        return;
      }
    }

    setFilterValues(emptyValues);
    setSearchError(null);
  };

  const tableRecords = React.useMemo(() => recordsData?.items ?? [], [recordsData]);
  const displayRecords = React.useMemo(
    () => limitDemoRecords(tableRecords, demoMode),
    [tableRecords, demoMode]
  );
  React.useEffect(() => {
    setTableFilteredRows(displayRecords);
  }, [displayRecords]);
  const mapping = React.useMemo(() => recordsData?.mapping ?? [], [recordsData]);
  const columns = React.useMemo(
    () => buildColumns(mapping, tableRecords[0]),
    [mapping, tableRecords]
  );

  const filters = React.useMemo(() => filtersData?.filters ?? [], [filtersData]);
  const fieldsById = new Map(fields.map(field => [field._id, field]));
  const mappedFilters = filters
    .map(filter => ({
      ...filter,
      fieldData: fieldsById.get(filter.field),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const currentDataset = datasets.find(dataset => dataset._id === datasetId);
  const currentSection =
    selectedSection ??
    (typeof currentDataset?.section === 'string' ? currentDataset?.section : undefined);
  // Separate filters by detail mode
  const { standardFilters, detailFilters } = React.useMemo(() => {
    return separateFiltersByDetailMode(mappedFilters);
  }, [mappedFilters]);

  // Get team restrictions for detailed mode
  const teamRestrictions = React.useMemo((): TeamDetailRestrictions | undefined => {
    // Prefer dedicated team endpoint (works for all users, not just admins)
    if (currentTeam && typeof currentTeam === 'object') {
      const detail = currentTeam.detail;
      if (detail && typeof detail === 'object') {
        return detail as TeamDetailRestrictions;
      }
    }
    // Fallback to account's teamData (works for admin users where team is populated)
    const teamData = (account as Record<string, unknown>)?.teamData;
    if (teamData && typeof teamData === 'object') {
      return (teamData as Record<string, unknown>).detail as TeamDetailRestrictions | undefined;
    }
    return undefined;
  }, [currentTeam, account]);

  // Resolve active table object (used for isDetailTable check)
  const activeTable = React.useMemo(
    () => tables.find(t => t._id === appliedTableId),
    [tables, appliedTableId]
  );

  // Check if user can access detailed mode (respects backend isDetailTable flag)
  const canUseDetailMode = React.useMemo(() => {
    return canAccessDetailedMode(account as Record<string, unknown>, teamRestrictions, activeTable);
  }, [account, teamRestrictions, activeTable]);

  // Check if user should use demo mode when detail is active
  const shouldUseDemo = React.useMemo(() => {
    return shouldUseDemoMode(account as Record<string, unknown>, teamRestrictions);
  }, [account, teamRestrictions]);

  // Toggle detailed mode handler
  const handleToggleDetailMode = React.useCallback(() => {
    setDetailActived(prev => {
      const newValue = !prev;
      // If turning off detail mode, also turn off demo mode
      if (!newValue) {
        setDemoMode(false);
      } else if (shouldUseDemo) {
        // If turning on and user should use demo, enable demo mode
        setDemoMode(true);
      }
      return newValue;
    });
  }, [setDemoMode, setDetailActived, shouldUseDemo]);

  const filtersToShow = React.useMemo(() => {
    // Combine standard filters with detail filters when detail mode is active
    const baseFilters = detailActived ? [...standardFilters, ...detailFilters] : standardFilters;

    const visibleFilters = baseFilters.filter(filter => !filter.isCalculating);
    const featured = visibleFilters.filter(filter => filter.featured);
    const nonFeatured = visibleFilters.filter(filter => !filter.featured);
    const ordered = [...featured, ...nonFeatured];
    if (moreFiltersActive) return ordered;
    return ordered.slice(0, 6);
  }, [standardFilters, detailFilters, detailActived, moreFiltersActive]);

  const tableDateRange = React.useMemo((): DateRange | null => {
    const currentTable = tables.find(t => t._id === appliedTableId);
    if (!currentTable?.name) return null;
    return parseDateRangeFromTableName(currentTable.name);
  }, [tables, appliedTableId]);

  const yearQuickSelects = React.useMemo(() => {
    return generateYearQuickSelects(tableDateRange);
  }, [tableDateRange]);

  // Get user's saved column settings from account data
  const userTableSettings = React.useMemo((): TableColumnSettings[] => {
    const settings = (account as Record<string, unknown>)?.settings;
    if (
      settings &&
      typeof settings === 'object' &&
      'tables' in settings &&
      Array.isArray((settings as Record<string, unknown>).tables)
    ) {
      return (settings as Record<string, unknown>).tables as TableColumnSettings[];
    }
    return [];
  }, [account]);

  // Find saved column settings for current table
  const savedColumnSettings = React.useMemo(() => {
    if (!appliedTableId) return null;
    return userTableSettings.find(setting => setting.id === appliedTableId) ?? null;
  }, [userTableSettings, appliedTableId]);

  // Get all column names from the mapping
  const allColumnNames = React.useMemo(() => {
    return mapping.map(getMappingColumnName).filter(Boolean);
  }, [mapping]);

  // Compute initial column visibility based on saved settings or field defaults
  const initialColumnVisibility = React.useMemo((): VisibilityState => {
    if (!allColumnNames.length) return {};

    // If user has saved settings for this table, use them
    if (savedColumnSettings?.columns?.length) {
      const computed = columnsToVisibilityState(allColumnNames, savedColumnSettings.columns);
      // Guard: if saved columns are stale and none match current columns, show all
      const hasAnyVisible = Object.values(computed).some(v => v);
      if (!hasAnyVisible) return {};
      return computed;
    }

    // Default: all columns visible
    return {};
  }, [allColumnNames, savedColumnSettings]);

  React.useEffect(() => {
    setActiveColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility, activeTableId]);

  // Handle column visibility changes - save to backend
  const handleColumnVisibilityChange = React.useCallback(
    (state: VisibilityState) => {
      if (!appliedTableId) return;
      setActiveColumnVisibility(state);
      setSavedReport(false);

      // Resolve implicit visibility: columns not in the state are visible by default
      const visibleColumns = allColumnNames.filter(col => state[col] !== false);

      // Update the settings for this table
      const existingSettings = userTableSettings.filter(s => s.id !== appliedTableId);
      const newSettings: TableColumnSettings[] = [
        ...existingSettings,
        { id: appliedTableId, columns: visibleColumns },
      ];

      updateSettingsMutation.mutate({ tables: newSettings });
    },
    [appliedTableId, allColumnNames, userTableSettings, updateSettingsMutation]
  );

  const handleYearQuickSelect = React.useCallback(
    (range: DateRange, filterId: string) => {
      setFilterValues(prev => ({
        ...prev,
        [filterId]: {
          ...(prev[filterId] ?? createDefaultFilterValueState()),
          min: range.minDate,
          max: range.maxDate,
          touched: true,
        },
      }));
    },
    [setFilterValues]
  );

  React.useEffect(() => {
    if (!mappedFilters.length) return;
    setFilterValues(prev => {
      const next = { ...prev };
      let createdState = false;
      mappedFilters.forEach(filter => {
        if (!next[filter._id]) {
          next[filter._id] = createDefaultFilterValueState();
          createdState = true;
        }
      });

      if (!createdState) {
        return prev;
      }

      // Apply date range from current table name to date filters
      const currentTable = tables.find(t => t._id === appliedTableId);
      if (currentTable?.name) {
        const dateRange = parseDateRangeFromTableName(currentTable.name);
        if (dateRange) {
          return applyDateRangeToFilters(mappedFilters, next, dateRange);
        }
      }

      return next;
    });
  }, [mappedFilters, appliedTableId, tables]);

  const fetchFilterOptions = React.useCallback(
    async (filter: TableFilter, query: string) => {
      if (!filter.relationship?.table) return;
      setFilterValues(prev => ({
        ...prev,
        [filter._id]: {
          ...(prev[filter._id] ?? createDefaultFilterValueState()),
          isFetching: true,
        },
      }));
      try {
        const data = await fetchAutocompleteOptions({
          tableId: filter.relationship.table ?? '',
          fieldId: filter.relationship.field,
          toShowId: filter.relationship.toShow,
          query,
          limit: LOOKUP_FILTER_OPTIONS_LIMIT,
        });
        const items = data
          .map(item => {
            const rawValue = item.value ?? item._id ?? item.name ?? '';
            const value =
              typeof rawValue === 'string' || typeof rawValue === 'number' ? String(rawValue) : '';
            const rawName = item.name;
            const rawItemValue = item.value;
            const nameStr =
              typeof rawName === 'string' || typeof rawName === 'number' ? String(rawName) : '';
            const valueStr =
              typeof rawItemValue === 'string' || typeof rawItemValue === 'number'
                ? String(rawItemValue)
                : '';
            const fallbackName = nameStr || valueStr || value;
            const displayLabel =
              nameStr && value && nameStr !== value ? `${nameStr} (${value})` : fallbackName;
            const selectedLabel =
              nameStr && value && nameStr !== value && isNumericIdentifier(value)
                ? formatLookupSelectionLabel(nameStr, value)
                : nameStr && value && nameStr !== value
                  ? `${nameStr} (${value})`
                  : fallbackName;

            return {
              value,
              label: fallbackName,
              displayLabel,
              selectedLabel,
              name: nameStr || undefined,
              idValue: value,
            };
          })
          .slice(0, LOOKUP_FILTER_OPTIONS_LIMIT);
        setFilterValues(prev => ({
          ...prev,
          [filter._id]: {
            ...(prev[filter._id] ?? createDefaultFilterValueState()),
            items,
            selectedLabels: (prev[filter._id]?.selectedValues ?? []).reduce<Record<string, string>>(
              (acc, selectedValue) => {
                const matched = items.find(item => item.value === selectedValue);
                acc[selectedValue] =
                  matched?.selectedLabel ??
                  matched?.displayLabel ??
                  acc[selectedValue] ??
                  selectedValue;
                return acc;
              },
              { ...(prev[filter._id]?.selectedLabels ?? {}) }
            ),
            isFetching: false,
          },
        }));
      } catch {
        setFilterValues(prev => ({
          ...prev,
          [filter._id]: {
            ...(prev[filter._id] ?? createDefaultFilterValueState()),
            isFetching: false,
          },
        }));
      }
    },
    [setFilterValues]
  );

  const updateFilterState = React.useCallback(
    (filterId: string, updater: (current: FilterValueState) => FilterValueState) => {
      setFilterValues(prev => {
        const current = prev[filterId] ?? createDefaultFilterValueState();
        const nextState = updater(current);
        return {
          ...prev,
          [filterId]: nextState,
        };
      });
    },
    [setFilterValues]
  );

  const buildQueryWrapper = React.useCallback(
    (
      filtersList: Array<TableFilter & { fieldData?: TableField }>,
      valuesOverride?: Record<string, FilterValueState>
    ) => {
      const activeValues = valuesOverride ?? filterValues;
      return buildQueryFromFilters(filtersList, activeValues);
    },
    [filterValues]
  );

  React.useEffect(() => {
    setSavedReport(false);
    setSaveFeedback(null);
    setSaveError(null);
  }, [filterValues]);

  const filtersDisabled = !appliedTableId || switchAccountMutation.isPending;
  const isSearchLoading = searchRequested && recordsFetching;
  const exportDisabled = filtersDisabled || fieldsLoading || tablesLoading;
  const accountMaxReport = typeof account?.maxReport === 'number' ? account.maxReport : undefined;
  const totalItems = recordsData?.total_items ?? tableRecords.length;
  const withinReportLimit =
    typeof accountMaxReport === 'number' ? totalItems <= accountMaxReport : true;
  const canSaveReport = !filtersDisabled && !fieldsLoading && !tablesLoading && withinReportLimit;

  const setExportError = React.useCallback(
    (message: string) => {
      setSearchError(message);
      if (!hasSearchRun) setHasSearchRun(true);
    },
    [hasSearchRun, setHasSearchRun, setSearchError]
  );

  const clearExportTimer = React.useCallback(() => {
    if (exportTimerRef.current !== null) {
      window.clearInterval(exportTimerRef.current);
      exportTimerRef.current = null;
    }
  }, []);

  const buildReportFilters = React.useCallback(() => {
    return mappedFilters
      .map(filter => {
        const state = filterValues[filter._id];
        if (!state) return null;
        let filterValue: string | string[] | undefined;

        if (filter.filter === 'date' && filter.values === 'range') {
          if (!state.min || !state.max) return null;
          filterValue = `${state.min}|${state.max}`;
        } else if (filter.filter === 'date' || filter.filter === 'numeric') {
          if (!state.inputValue) return null;
          filterValue = state.inputValue;
        } else if (filter.multiple) {
          if (!state.selectedValues.length) return null;
          filterValue = state.selectedValues;
        } else {
          if (!state.inputValue) return null;
          filterValue = state.inputValue;
        }

        if (filterValue === undefined) return null;

        let value: string | string[] = filterValue;
        if (filter.filter === 'company' && filterValue) {
          if (Array.isArray(filterValue)) {
            value = filterValue.filter(item => !/^\d+$/.test(item));
          }
        }

        return {
          ...filter,
          field: filter.field,
          value,
          conditions: filter.values,
          filterValue,
        };
      })
      .filter((item): item is ActiveFilterPayload => item !== null);
  }, [mappedFilters, filterValues]);

  const getVisibleColumnNames = React.useCallback(() => {
    if (!allColumnNames.length) return [];
    const hasExplicitVisibility = Object.keys(activeColumnVisibility).length > 0;
    if (!hasExplicitVisibility) return allColumnNames;
    return visibilityStateToColumns(activeColumnVisibility);
  }, [activeColumnVisibility, allColumnNames]);

  const getExportColumnNames = React.useCallback(() => {
    if (!allColumnNames.length) return [];
    const hasExplicitVisibility = Object.keys(activeColumnVisibility).length > 0;
    if (!hasExplicitVisibility) return allColumnNames;
    const explicitVisibleColumns = visibilityStateToColumns(activeColumnVisibility);
    return explicitVisibleColumns.length ? explicitVisibleColumns : allColumnNames;
  }, [activeColumnVisibility, allColumnNames]);

  const buildReportColumns = React.useCallback(() => {
    const visibleColumnNames = getVisibleColumnNames();
    const hasExplicitVisibility = Object.keys(activeColumnVisibility).length > 0;
    if (hasExplicitVisibility && !visibleColumnNames.length) {
      return [];
    }

    const visibleSet = new Set(visibleColumnNames);
    const selectedMappingIds = mapping
      .filter(column => {
        const name = getMappingColumnName(column);
        return Boolean(name) && visibleSet.has(name);
      })
      .map(column => column._id)
      .filter((id): id is string => Boolean(id));

    if (selectedMappingIds.length) {
      return selectedMappingIds.map(id => ({ field: id }));
    }

    if (!mapping.length) {
      const fieldIds = fields.map(field => field._id).filter((id): id is string => Boolean(id));
      return fieldIds.map(id => ({ field: id }));
    }

    return [];
  }, [activeColumnVisibility, fields, getVisibleColumnNames, mapping]);

  const buildReportPayload = React.useCallback(
    (reportName: string, options: { generate: boolean }) => {
      if (!activeTableId) {
        setExportError('Selecciona una tabla para guardar el reporte.');
        return null;
      }

      if (!reportName.trim()) {
        setExportError('Para guardar reporte, debes agregar un nombre.');
        return null;
      }

      const columnsPayload = buildReportColumns();
      if (!columnsPayload.length) {
        setExportError('Para guardar reporte, debes seleccionar al menos una columna.');
        return null;
      }

      return {
        name: reportName.trim(),
        dataset: datasetId,
        table: activeTableId,
        filters: buildReportFilters(),
        columns: columnsPayload,
        analysis: [],
        detailed: detailActived ? 1 : 0,
        demoMode,
        generate: options.generate,
      };
    },
    [
      activeTableId,
      buildReportColumns,
      buildReportFilters,
      datasetId,
      demoMode,
      detailActived,
      setExportError,
    ]
  );

  const resolveReportFileUrl = React.useCallback((file: string) => {
    if (!file) return '';
    if (file.startsWith('http://') || file.startsWith('https://')) return file;
    if (file.startsWith('//')) return `${window.location.protocol}${file}`;
    if (file.startsWith('/')) return `${window.location.origin}${file}`;
    return `${window.location.protocol}//${file}`;
  }, []);

  const handleExportFile = React.useCallback(() => {
    const fileUrl = exportFileRef.current;
    if (!fileUrl) return;
    window.location.href = fileUrl;
    exportFileRef.current = null;

    if (userId && activeTableId) {
      const payload = {
        user: userId,
        event: 'Descarga',
        module: 'Reports',
        value: {
          fields: buildFieldsPayload(mappedFilters, filterValues),
          table: activeTableId,
          dataset: datasetId,
        },
      };
      void postAnalyticsEvent(payload).catch(() => null);
    }
  }, [activeTableId, datasetId, filterValues, mappedFilters, userId]);

  const checkExportStatus = async () => {
    try {
      const response = await fetchReportStatus();
      const status = response?.status;
      const resolvedFilePath = extractReportFilePath(response);
      if (resolvedFilePath) {
        exportFileRef.current = resolveReportFileUrl(resolvedFilePath);
      }

      if (status === 'processing') return;

      if (status === 'error') {
        setExportError(
          'No fue posible generar el reporte. Valida los filtros e intenta nuevamente.'
        );
        clearExportTimer();
        setIsExportingReport(false);
        return;
      }

      if (status === 'success') {
        clearExportTimer();
        setIsExportingReport(false);
        if (!exportFileRef.current) {
          setExportError('El reporte se generó, pero no se recibió la URL del archivo.');
          return;
        }
        handleExportFile();
        return;
      }

      if (typeof status === 'string') {
        setExportError(status);
      }
    } catch {
      clearExportTimer();
      setIsExportingReport(false);
      setExportError('No se pudo completar la exportación.');
    }
  };

  const handleSearch = () => {
    const dateError = validateDateRange(mappedFilters, filterValues);
    if (dateError) {
      setSearchError(dateError);
      setHasSearchRun(true);
      return;
    }
    const query = buildQueryWrapper(mappedFilters);
    const fieldsPayload = buildFieldsPayload(mappedFilters, filterValues);
    const fieldInfo = buildFieldInfo(mappedFilters, fields, filterValues);
    setSearchPayload({
      fields: fieldsPayload,
      fieldInfo,
      table: activeTableId,
      dataset: datasetId,
    });
    setSearchError(null);
    setSearchRequested(true);
    setHasSearchRun(true);
    setAppliedQuery(query);
    setSearchTrigger(prev => prev + 1);
  };

  const _handleExportReport = async () => {
    if (isExportingReport) return;
    const dateError = validateDateRange(mappedFilters, filterValues);
    if (dateError) {
      setExportError(dateError);
      return;
    }

    const reportName = `Reporte temporal para Excel ${formatReportTimestamp(new Date())}`;
    const payload = buildReportPayload(reportName, { generate: true });
    if (!payload) return;

    try {
      setIsExportingReport(true);
      setSearchError(null);
      const response = await createReport(payload);
      const file = extractReportFilePath(response);

      if (file) {
        exportFileRef.current = resolveReportFileUrl(file);
      }
      clearExportTimer();
      exportTimerRef.current = window.setInterval(() => {
        void checkExportStatus();
      }, 3000);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      const canRecoverWithPolling =
        isReportGenerationInProgressError(error) ||
        (typeof message === 'string' &&
          /load failed|failed to fetch|network/i.test(message.toLowerCase()));

      if (canRecoverWithPolling) {
        setExportError('Hay una exportación en curso. Validando estado...');
        clearExportTimer();
        exportTimerRef.current = window.setInterval(() => {
          void checkExportStatus();
        }, 3000);
        return;
      }

      setExportError(message || 'No se pudo generar el reporte.');
      setIsExportingReport(false);
    }
  };

  const handleExportBrowserExcel = React.useCallback(async () => {
    if (isExportingReport) return;

    if (!activeTableId) {
      setExportError('Selecciona una tabla para descargar el reporte.');
      return;
    }

    try {
      setIsExportingReport(true);
      setSearchError(null);
      const rowsToExport = tableFilteredRows;

      if (!rowsToExport.length) {
        setExportError('No hay datos disponibles para exportar.');
        return;
      }

      const mappingSource = mapping;
      const mappingByAccessor = new Map<string, ColumnMapping>();
      mappingSource.forEach(column => {
        const accessor = getMappingColumnName(column);
        if (!accessor || mappingByAccessor.has(accessor)) return;
        mappingByAccessor.set(accessor, column);
      });

      const visibleColumnNames = getExportColumnNames();
      const fallbackColumnNames = allColumnNames.length
        ? allColumnNames
        : mappingByAccessor.size
          ? Array.from(mappingByAccessor.keys())
          : Object.keys(rowsToExport[0] ?? {});
      const columnNames = visibleColumnNames.length ? visibleColumnNames : fallbackColumnNames;
      const exportColumnNames = Array.from(new Set(columnNames.filter(Boolean)));

      if (!exportColumnNames.length) {
        setExportError('No hay columnas disponibles para exportar.');
        return;
      }

      const exportColumns = exportColumnNames.map(accessor => {
        const mapped = mappingByAccessor.get(accessor);
        return {
          accessor,
          header: mapped
            ? getDisplayColumnHeader(mapped, mappingSource, rowsToExport[0])
            : accessor,
        };
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte');
      const headerRow = worksheet.getRow(1);
      exportColumns.forEach((column, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = column.header;
        cell.font = { bold: true, size: 10 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF3F4F6' },
        };
      });

      let currentRow = 2;
      rowsToExport.forEach(row => {
        const worksheetRow = worksheet.getRow(currentRow);
        exportColumns.forEach((column, index) => {
          worksheetRow.getCell(index + 1).value = toExcelCellValue(row[column.accessor]);
        });
        currentRow += 1;
      });

      worksheet.columns.forEach(column => {
        let maxLength = 10;
        column.eachCell?.({ includeEmpty: true }, cell => {
          const value = cell.value;
          const length =
            value == null
              ? 0
              : typeof value === 'string' || typeof value === 'number'
                ? String(value).length
                : 0;
          if (length > maxLength) {
            maxLength = Math.min(length + 2, 60);
          }
        });
        column.width = maxLength;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Daater_reporte_${activeTableId}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);

      if (userId) {
        const payload = {
          user: userId,
          event: 'Descarga',
          module: 'Reports',
          value: {
            table: activeTableId,
            dataset: datasetId,
            totalRows: rowsToExport.length,
            columns: exportColumns.map(column => column.accessor),
          },
        };
        void postAnalyticsEvent(payload).catch(() => null);
      }
    } catch (error) {
      const message = extractApiErrorMessage(error);
      setExportError(message || 'No se pudo exportar el archivo.');
    } finally {
      setIsExportingReport(false);
    }
  }, [
    activeTableId,
    allColumnNames,
    datasetId,
    getExportColumnNames,
    isExportingReport,
    mapping,
    setExportError,
    tableFilteredRows,
    userId,
  ]);

  const handleSaveReport = React.useCallback(
    async (reportName: string) => {
      if (isSavingReport) return false;

      const payload = buildReportPayload(reportName, { generate: false });
      if (!payload) {
        setSaveError(searchError || 'Completa el nombre y asegúrate de tener columnas visibles.');
        return false;
      }

      setSearchError(null);
      setSaveError(null);
      setSaveFeedback(null);

      try {
        setIsSavingReport(true);
        const createdReport = await createReport(payload);
        setSavedReport(true);
        setSaveFeedback('Búsqueda guardada y lista para reutilizar.');
        if (datasetId) {
          queryClient.setQueryData<SavedReportListResponse | undefined>(
            queryKeys.savedReports(datasetId),
            current => {
              const items = current?.items ?? [];
              const normalizedReport: SavedReportItem = {
                ...createdReport,
                dataset: createdReport.dataset ?? datasetId,
                table: createdReport.table ?? activeTableId,
                name: createdReport.name ?? payload.name,
                filters: createdReport.filters ?? payload.filters,
                columns: createdReport.columns ?? payload.columns,
                analysis: createdReport.analysis ?? payload.analysis,
                detailed: createdReport.detailed ?? payload.detailed,
                demoMode: createdReport.demoMode ?? payload.demoMode,
              };

              const deduped = items.filter(item => item._id !== normalizedReport._id);
              return {
                ...(current ?? {}),
                items: [normalizedReport, ...deduped],
              };
            }
          );
          await queryClient.invalidateQueries({ queryKey: queryKeys.savedReports(datasetId) });
        }
        return true;
      } catch (error) {
        const message = extractApiErrorMessage(error) || 'No se pudo guardar el reporte.';
        setSavedReport(false);
        setSaveError(message);
        return false;
      } finally {
        setIsSavingReport(false);
      }
    },
    [activeTableId, buildReportPayload, datasetId, isSavingReport, queryClient, searchError]
  );

  const handleOpenSavedReport = React.useCallback(
    async (report: SavedReportItem, fallbackDataset?: string) => {
      try {
        setOpeningSavedReportId(report._id);
        setSearchError(null);
        await openSavedSearch(report, fallbackDataset);
      } catch (error) {
        const message = extractApiErrorMessage(error) || 'No se pudo abrir la búsqueda guardada.';
        setSearchError(message);
      } finally {
        setOpeningSavedReportId(null);
      }
    },
    [openSavedSearch]
  );

  const handleDeleteSavedReport = React.useCallback(
    async (report: SavedReportItem) => {
      const confirmed = window.confirm(
        `¿Eliminar la búsqueda guardada "${report.name ?? 'Sin nombre'}"?`
      );
      if (!confirmed) return;

      try {
        setDeletingSavedReportId(report._id);
        setSearchError(null);
        await deleteSavedReportMutation.mutateAsync(report._id);
      } catch (error) {
        const message =
          extractApiErrorMessage(error) || 'No se pudo eliminar la búsqueda guardada.';
        setSearchError(message);
      } finally {
        setDeletingSavedReportId(null);
      }
    },
    [deleteSavedReportMutation]
  );

  React.useEffect(() => {
    if (!searchRequested) return;
    if (recordsLoading || recordsFetching) return;
    const items = recordsData?.items ?? [];
    if (recordsError instanceof ApiError && recordsError.status === 400) {
      setSearchError('Tu búsqueda retorna demasiados resultados para mostrar. Refina los filtros para reducir el volumen de datos y obtener resultados más precisos.');
    } else if (!items.length) {
      setSearchError('No se encontró información que coincidiera con los criterios de búsqueda.');
    }
    if (searchPayload && userId) {
      const isDaater = account?.team !== '5ba049380cd2a2709ee4c536';
      const payload = {
        user: userId,
        event: 'Busqueda',
        module: 'Reports',
        isDaater,
        detail: false,
        value: {
          ...searchPayload,
          result: items.length > 0,
        },
      };
      void postAnalyticsEvent(payload)
        .then(() => {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.historic(userId),
          });
        })
        .catch(() => null);
    }
    setSearchRequested(false);
  }, [
    searchRequested,
    recordsLoading,
    recordsFetching,
    recordsData,
    searchPayload,
    userId,
    queryClient,
    account?.team,
    recordsError,
  ]);

  React.useEffect(() => {
    if (!pendingHistoricSearch || historicApplied) return;
    if (!mappedFilters.length) return;
    setFilterValues(prev => {
      const nextValues = hydrateFiltersFromHistoric(mappedFilters, pendingHistoricSearch, prev);
      const query = buildQueryWrapper(mappedFilters, nextValues);
      setAppliedQuery(query);
      setHistoricApplied(true);
      setHasSearchRun(true);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(HISTORIC_STORAGE_KEY);
      }
      return nextValues;
    });
  }, [pendingHistoricSearch, historicApplied, mappedFilters, buildQueryWrapper]);

  React.useEffect(() => {
    if (!pendingSavedSearch || savedSearchApplied) return;
    if (!mappedFilters.length) return;
    if (pendingSavedSearch.table !== activeTableId) return;
    if (pendingSavedSearch.dataset !== datasetId) return;

    setFilterValues(prev => {
      const nextValues = hydrateFiltersFromHistoric(
        mappedFilters,
        { fields: pendingSavedSearch.fields },
        prev
      );
      const query = buildQueryWrapper(mappedFilters, nextValues);
      setAppliedQuery(query);
      setDetailActived(pendingSavedSearch.detailed);
      setDemoMode(pendingSavedSearch.demoMode);
      setActiveColumnVisibility(
        fieldIdsToVisibilityState(
          allColumnNames,
          mapping,
          fields,
          pendingSavedSearch.columnFieldIds
        )
      );
      setSavedSearchApplied(true);
      setHasSearchRun(true);
      setSavedReport(true);
      setSaveFeedback(`Búsqueda “${pendingSavedSearch.reportName}” cargada.`);
      setPendingSavedSearch(null);
      clearPendingSavedSearchPayload();
      return nextValues;
    });
  }, [
    activeTableId,
    allColumnNames,
    buildQueryWrapper,
    datasetId,
    fields,
    mapping,
    mappedFilters,
    pendingSavedSearch,
    savedSearchApplied,
    setDemoMode,
    setDetailActived,
  ]);

  const renderFilterField = React.useCallback(
    (filter: TableFilter & { fieldData?: TableField }) => {
      const filterKey = filter._id ?? filter.field;
      return (
        <div key={filterKey}>
          <FilterFieldRenderer
            filter={filter}
            state={filterValues[filter._id]}
            inputClassName={inputClassName}
            filtersDisabled={filtersDisabled}
            updateFilterState={updateFilterState}
            fetchFilterOptions={fetchFilterOptions}
            yearQuickSelects={yearQuickSelects}
            tableDateRange={tableDateRange}
            onYearQuickSelect={handleYearQuickSelect}
          />
        </div>
      );
    },
    [
      fetchFilterOptions,
      filterValues,
      filtersDisabled,
      handleYearQuickSelect,
      tableDateRange,
      updateFilterState,
      yearQuickSelects,
    ]
  );

  return (
    <>
      <style>{`
        body.hide-header-desktop header {
          display: block;
        }
        @media (min-width: 1024px) {
          body.hide-header-desktop header {
            display: none;
          }
        }
      `}</style>
      <div className="h-screen overflow-hidden bg-background-light font-body">
        {/* Mobile backdrop */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <div className="flex flex-col h-full lg:flex-row">
          <aside
            className={`fixed inset-y-0 left-0 z-50 h-screen w-72 border transition-transform duration-300 lg:relative lg:z-30 lg:border-b-0 lg:border-r lg:transition-none ${sidebarClasses.container} ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0 ${sidebarCollapsed ? 'lg:w-24' : 'lg:w-72'}`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between gap-3 px-4 py-5 lg:px-5">
                <DaaterLogoHomeLink
                  alt="Daater logo"
                  className="inline-flex"
                  imgClassName={`${sidebarClasses.logo} ${
                    sidebarCollapsed ? 'h-10 w-10 object-contain' : 'h-9 w-auto object-contain'
                  }`}
                />
                <div className="flex items-center gap-2">
                  {/* Mobile close button */}
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`inline-flex items-center justify-center rounded-full border p-1.5 transition lg:hidden ${sidebarClasses.iconButton}`}
                    aria-label="Cerrar menú"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {/* Desktop collapse button */}
                  <button
                    type="button"
                    onClick={() => setSidebarCollapsed(prev => !prev)}
                    className={`hidden items-center justify-center rounded-full border p-1.5 transition lg:inline-flex ${sidebarClasses.iconButton}`}
                    aria-label={sidebarCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
                  >
                    {sidebarCollapsed ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : (
                      <ChevronLeft className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col flex-1 gap-8 px-4 pt-4 pb-6 overflow-y-auto lg:px-5">
                <div className={sidebarCollapsed ? 'hidden' : ''}>
                  {sectionData.list.length ? (
                    <div>
                      <p
                        className={`mb-3 text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.sectionLabel}`}
                      >
                        Pais
                      </p>
                      <div className="relative">
                        <span className="absolute inset-y-0 flex items-center pointer-events-none left-2">
                          <span
                            className={`relative flex h-5 w-5 items-center justify-center rounded-md ${sidebarClasses.selectFlag}`}
                          >
                            <span className="relative z-10 text-base">
                              {getCountryFlag(currentSection ?? 'General')}
                            </span>
                          </span>
                        </span>
                        <select
                          className={`w-full appearance-none rounded-md border py-2 pl-7 pr-6 text-sm ${sidebarClasses.select} [&>option]:bg-white [&>option]:text-gray-900`}
                          value={selectedSection ?? ''}
                          disabled={datasetsLoading}
                          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            setSelectedSection(event.target.value);
                            setSelectedDataset(undefined);
                            setAppliedTableId(undefined);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {sectionData.list.map(section => (
                            <option key={section} value={section}>
                              {section}
                            </option>
                          ))}
                        </select>
                        <span
                          className={`absolute inset-y-0 flex items-center pointer-events-none right-2 ${sidebarClasses.selectCaret}`}
                        >
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <path d="M6 8l4 4 4-4" strokeLinecap="round" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={sidebarCollapsed ? 'hidden' : ''}>
                  <div className={`pb-2 mb-4 border-b ${sidebarClasses.baseDivider}`}>
                    <p
                      className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.sectionLabel}`}
                    >
                      Base
                    </p>
                  </div>
                  <div
                    className="flex flex-col gap-2"
                    role="tablist"
                    aria-label="Bases disponibles"
                  >
                    {datasetsForSection.length ? (
                      datasetsForSection.map(dataset => {
                        const isActive = dataset._id === datasetId;
                        return (
                          <button
                            key={dataset._id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={datasetsLoading}
                            onClick={() => {
                              setSelectedDataset(dataset._id);
                              setAppliedTableId(undefined);
                              setMobileMenuOpen(false);
                            }}
                            className={`${sidebarTabClassName} ${
                              isActive ? sidebarClasses.baseActive : sidebarClasses.baseInactive
                            }`}
                          >
                            <ChartBarBigIcon className={sidebarClasses.baseIcon} />
                            {/* <FileGlass className="size-4"></FileGlass> */}
                            {dataset.name ?? dataset._id}
                          </button>
                        );
                      })
                    ) : (
                      <span className={`text-sm ${sidebarClasses.emptyText}`}>
                        Sin bases disponibles.
                      </span>
                    )}
                  </div>
                </div>

                {sidebarCollapsed ? (
                  <div className="hidden lg:flex lg:flex-col lg:gap-6 lg:pt-2">
                    {sectionData.list.length ? (
                      <div className="flex flex-col items-center gap-2">
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.collapsedLabel}`}
                        >
                          País
                        </span>
                        <div
                          className={`flex items-center justify-center text-xl border rounded-full h-9 w-9 shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)] ${sidebarClasses.collapsedFlag}`}
                        >
                          {getCountryFlag(currentSection ?? 'General')}
                        </div>
                      </div>
                    ) : null}
                    <div className="flex flex-col items-center gap-2">
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.collapsedLabel}`}
                      >
                        Base
                      </span>
                      <div className="flex flex-col items-center gap-2">
                        {datasetsForSection.length ? (
                          datasetsForSection.map(dataset => {
                            const isActive = dataset._id === datasetId;
                            const label = dataset.name ?? dataset._id ?? 'Base';
                            return (
                              <button
                                key={dataset._id}
                                type="button"
                                title={label}
                                aria-label={label}
                                disabled={datasetsLoading}
                                onClick={() => {
                                  setSelectedDataset(dataset._id);
                                  setAppliedTableId(undefined);
                                  setMobileMenuOpen(false);
                                }}
                                className={`flex h-9 w-9 items-center justify-center rounded-full border shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)] ${
                                  isActive
                                    ? sidebarClasses.collapsedBaseActive
                                    : sidebarClasses.collapsedBaseInactive
                                }`}
                              >
                                <FileSpreadsheet className="w-4 h-4" />
                              </button>
                            );
                          })
                        ) : (
                          <span className={`text-xs ${sidebarClasses.emptyText}`}>-</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className={`flex-1 ${sidebarCollapsed ? 'hidden lg:block' : ''}`} />
              </div>

              <div
                className={`mt-auto px-4 pb-6 lg:px-5 ${
                  sidebarCollapsed ? 'lg:flex lg:justify-center' : ''
                }`}
              >
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(prev => !prev)}
                    className={`flex w-full items-center gap-3 rounded-full border px-3 py-2 text-sm transition ${sidebarClasses.menuButton} ${
                      sidebarCollapsed ? 'justify-center px-2' : ''
                    }`}
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                  >
                    <div className="flex items-center justify-center text-xs font-semibold bg-white/90 rounded-full h-9 w-9 shrink-0 text-primary aspect-square shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)]">
                      {initials}
                    </div>
                    {sidebarCollapsed ? null : (
                      <div className="flex items-center flex-1 min-w-0 gap-2">
                        <div className="min-w-0">
                          <span
                            className={`block text-sm font-medium truncate ${
                              isWhiteBlueSidebar ? 'text-slate-900' : 'text-white'
                            }`}
                          >
                            {displayName}
                          </span>
                          <span
                            className={`block truncate text-[11px] ${sidebarClasses.menuSubText}`}
                          >
                            {accountEmail}
                          </span>
                        </div>
                        <ChevronDown className={`ml-auto h-4 w-4 ${sidebarClasses.menuChevron}`} />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 overflow-y-auto">
            {/* Sticky navigation bar with breadcrumb and tabs */}
            <div className="sticky z-20 top-2">
              <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="flex flex-col gap-3 rounded-xl bg-white px-4 py-3 shadow-[0_4px_20px_-4px_rgba(26,46,88,0.25)] sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  {/* Breadcrumb */}
                  <nav aria-label="Breadcrumb" className="flex-shrink-0 min-w-0">
                    <ol className="flex items-center gap-2 text-xs">
                      <li className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-primary/10">
                          {getCountryFlag(currentSection ?? 'General')}
                        </span>
                        <span className="font-medium text-gray-700">
                          {currentSection ?? 'Pais'}
                        </span>
                      </li>
                      <li className="flex items-center gap-2 text-gray-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="truncate font-medium text-gray-900 max-w-[150px] sm:max-w-[200px]">
                          {currentDataset?.name ?? datasetId ?? ''}
                        </span>
                      </li>
                    </ol>
                  </nav>

                  {/* Tabs */}
                  <div
                    className={`flex flex-wrap items-center gap-1.5 rounded-lg border p-1 ${
                      detailActived
                        ? 'border-orange-200/80 bg-orange-50/70'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                    role="tablist"
                    aria-label="Tablas disponibles"
                  >
                    {tables.length ? (
                      tables.map(table => {
                        const isActive = table._id === appliedTableId;
                        return (
                          <button
                            key={table._id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={tablesLoading || switchAccountMutation.isPending}
                            onClick={() => void handleTableApply(table._id)}
                            className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-all whitespace-nowrap ${
                              isActive
                                ? detailActived
                                  ? 'bg-orange-500 text-white shadow-sm'
                                  : 'bg-primary text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            {table.name ?? table._id}
                          </button>
                        );
                      })
                    ) : (
                      <span className="px-3 py-1.5 text-xs text-gray-500">
                        {tablesLoading ? 'Cargando...' : 'Sin tablas'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <section className={`pt-8 pb-28 `}>
              {/*<section className={`pt-8 pb-28 `}>*/}
              <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                {/* Mobile hamburger button */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(true)}
                  className="mb-6 inline-flex items-center justify-center rounded-lg bg-white/10 p-2.5 text-white backdrop-blur-sm transition hover:bg-white/20 lg:hidden"
                  aria-label="Abrir menú"
                >
                  <Menu className="w-5 h-5" />
                </button>

                <div className="p-6 bg-white border border-transparent shadow-xl rounded-xl md:p-8">
                  {filtersLoading || fieldsLoading ? (
                    <p className="text-sm text-slate">Cargando filtros...</p>
                  ) : filtersToShow.length ? (
                    <div className={`space-y-6 ${filtersDisabled ? 'opacity-60' : ''}`}>
                      <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
                        {filtersToShow.map(filter => renderFilterField(filter))}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
                        {/* Detail mode toggle - only shown if user has access */}
                        <div className="flex items-center">
                          {canUseDetailMode && (
                            <DetailModeSwitch
                              checked={detailActived}
                              onChange={handleToggleDetailMode}
                              disabled={filtersDisabled}
                            />
                          )}
                        </div>

                        {/* Filter action buttons */}
                        <div className="flex items-center gap-6">
                          <button
                            className="text-sm font-medium text-slate hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                            type="button"
                            disabled={filtersDisabled}
                            onClick={handleClearFilters}
                          >
                            Limpiar
                          </button>
                          <button
                            className="text-sm font-semibold text-primary hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                            type="button"
                            onClick={() => setMoreFiltersActive(prev => !prev)}
                            disabled={filtersDisabled}
                          >
                            {moreFiltersActive ? 'Menos filtros' : 'Más filtros'}
                          </button>
                          <button
                            className={`flex h-12 w-12 items-center justify-center text-white transition rounded-full shadow-lg hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 ${
                              detailActived ? 'bg-orange-500 hover:bg-orange-600' : 'bg-primary'
                            }`}
                            type="button"
                            onClick={handleSearch}
                            disabled={filtersDisabled || isSearchLoading}
                          >
                            {isSearchLoading ? (
                              <span className="inline-flex h-5 w-5 border-2 rounded-full animate-spin border-white/30 border-t-white" />
                            ) : (
                              <svg
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                              >
                                <circle cx="9" cy="9" r="5.5" />
                                <path d="M13.5 13.5L18 18" strokeLinecap="round" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate">
                      {appliedTableId
                        ? 'No hay filtros configurados para esta tabla.'
                        : 'Selecciona el país, la base y si buscas exportaciones o importaciones para habilitar los filtros.'}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <div className="pb-20 -mt-16">
              <div className="w-full mx-auto max-w-7xl sm:px-6 lg:px-8">
                {!hasSearchRun ? (
                  <div className="relative mb-8 -mt-4 px-4 sm:px-0">
                    <SearchCards currentTableId={appliedTableId} />
                  </div>
                ) : null}
                {demoMode && hasSearchRun ? (
                  <div className="px-4 py-4 mx-4 mb-6 text-sm border rounded-lg bg-amber-50 border-amber-200 text-amber-800 sm:mx-0">
                    <p className="mb-2 font-bold">Bienvenido a la DEMO de DAATER Detalle.</p>
                    <p className="mb-2">
                      La primera plataforma que incluye: Modelo, Marca, Referencia, Principio
                      activo, Concentración y los detalles que llevabas años buscando.
                    </p>
                    <p className="mb-2">
                      Mostrando los 5 primeros resultados. Para ver todos los resultados contacta a
                      su asesor y comience a ver los detalles de las importaciones de todo su
                      mercado y competidores en solo 5 minutos.
                    </p>
                    <p className="font-medium">
                      Tel + 57 2 488 22 54, Cel + 57 301 635 90 17 Email: soporte@daater.com
                    </p>
                  </div>
                ) : null}
                {hasSearchRun && searchError ? (
                  <div className="px-4 py-3 mx-4 mb-6 text-sm bg-white border rounded-lg border-darkish-pink/30 text-darkish-pink sm:mx-0">
                    {searchError}
                  </div>
                ) : null}
                {hasSearchRun ? (
                  <div className="space-y-8">
                    <AnalysisSection
                      records={displayRecords}
                      mapping={mapping}
                      fields={fields}
                      filters={filters}
                      loading={recordsLoading || recordsFetching}
                      storageKey={activeTableId}
                    />
                    <DataSection
                      data={displayRecords}
                      columns={columns}
                      loading={recordsLoading || recordsFetching}
                      totalItems={recordsData?.total_items}
                      // Flujo legacy de export backend (deshabilitado):
                      // onDownload={() => void _handleExportReport()}
                      onDownload={() => void handleExportBrowserExcel()}
                      isDownloading={isExportingReport}
                      downloadDisabled={exportDisabled || isExportingReport || demoMode}
                      onSave={handleSaveReport}
                      saveError={saveError}
                      saveSuccessMessage={saveFeedback}
                      isSaving={isSavingReport}
                      saveDisabled={!canSaveReport || demoMode}
                      saved={savedReport}
                      initialColumnVisibility={initialColumnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onFilteredDataChange={setTableFilteredRows}
                    />
                    <SearchCards currentTableId={appliedTableId} />
                    <SavedSearchesSection
                      reports={savedReports}
                      datasets={datasets}
                      currentDatasetId={datasetId}
                      currentTableId={appliedTableId}
                      loading={savedReportsLoading}
                      openingReportId={openingSavedReportId}
                      deletingReportId={deletingSavedReportId}
                      onOpen={handleOpenSavedReport}
                      onDelete={handleDeleteSavedReport}
                      compact
                      emptyMessage="Aún no has guardado búsquedas para esta base."
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div
          ref={popupRef}
          className="fixed z-[60] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-pale-grey-three bg-white p-4 text-slate shadow-xl"
          style={{ bottom: popupPosition.bottom, left: popupPosition.left }}
          role="menu"
        >
          <div className="pb-4 mb-4 border-b border-pale-grey-three">
            <p className="text-sm font-semibold text-charcoal-grey">{displayName}</p>
            <div className="mt-3 overflow-hidden border rounded-lg border-pale-grey-three">
              <div className="grid grid-cols-3 text-xs">
                <div className="col-span-1 border-b border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                  Usuario
                </div>
                <div className="col-span-2 border-b border-pale-grey-three p-1.5 text-charcoal-grey">
                  {accountEmail}
                </div>
                <div className="col-span-1 border-b border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                  Tipo Usuario
                </div>
                <div className="col-span-2 border-b border-pale-grey-three p-1.5 text-charcoal-grey">
                  {accountPlan &&
                  typeof accountPlan === 'object' &&
                  'profile' in accountPlan &&
                  (typeof (accountPlan as Record<string, unknown>).profile === 'string' ||
                    typeof (accountPlan as Record<string, unknown>).profile === 'number')
                    ? String((accountPlan as Record<string, unknown>).profile)
                    : '-'}
                </div>
                <div className="col-span-1 border-b border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                  Plan
                </div>
                <div className="col-span-2 border-b border-pale-grey-three p-1.5 text-charcoal-grey">
                  {accountPlan &&
                  typeof accountPlan === 'object' &&
                  'name' in accountPlan &&
                  (typeof (accountPlan as Record<string, unknown>).name === 'string' ||
                    typeof (accountPlan as Record<string, unknown>).name === 'number')
                    ? String((accountPlan as Record<string, unknown>).name)
                    : '-'}
                </div>
                <div className="col-span-1 bg-pale-grey p-1.5 font-semibold text-slate">
                  Días restantes
                </div>
                <div className="col-span-1 p-1.5 text-charcoal-grey">
                  {typeof accountDaysLeft === 'number' ? accountDaysLeft : '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <a
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-primary hover:border-primary/40 hover:bg-primary/5"
                href="https://daater.com/precios-y-planes/"
                target="_blank"
                rel="noreferrer"
              >
                <Package className="w-4 h-4" />
                Planes
              </a>
              <button
                type="button"
                className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-slate hover:border-primary/40 hover:bg-primary/5"
                onClick={() => {
                  setMenuOpen(false);
                  void navigate({ to: '/payment-history' });
                }}
              >
                <CreditCard className="w-4 h-4" />
                Pagos
              </button>
            </div>
            <button
              type="button"
              className="flex items-center justify-start w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-darkish-pink/30 text-darkish-pink hover:bg-darkish-pink/5"
              onClick={() => {
                setMenuOpen(false);
                clearAuth();
                clearAccount();
                queryClient.clear();
                clearDashboardSession();
                void navigate({ to: '/login' });
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesion
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
